import React, { useContext, useState, useEffect } from 'react';
import { ArnContext } from 'contexts';
import { FormContext } from 'contexts/FormContext';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { Button, Loader } from 'components/common';
import FileUpload from './FileUpload';
import {
  ApplicationName,
  ControlInputGroup,
  FormNavigation
} from 'components/CASAForm';
import {
  AuTextInput,
  AuDateInput,
  AuControlInput,
  AuCallout,
  AuFormItemWrapper,
  AuDirectionLink,
  AuErrorText,
  AuSelect
} from 'components/DTA';
import { ArnApplicationDvsMethodFormData } from 'types/forms/arnApplicationTypes';
import { IFormAlertType } from 'types/forms/formTypes';

interface ForeignPassportDetailsProps {
  handleNextAction: () => void;
};

const ForeignPassportDetails: React.FC<ForeignPassportDetailsProps> = ({ handleNextAction }) => {
  const [showDetail, setShowDetails] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);

  const { handleBack, setFormAlert, setOnChangeValidation, openStepById } = useContext(FormContext);
  const { errors, setFieldValue, getFieldMeta } = useFormikContext();
  const { referenceDataResponse: countryListResponse } = useContext(ArnContext);
  const { values, validateForm, setErrors } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const { setPreviousStep } = useContext(ArnContext);

  const declarationFieldError = get(errors, 'foreignPassportDetails.declaration');

  useEffect(() => {
    setFieldValue('foreignPassportDetails.isManualUpload', false);
    if (values.foreignPassportDetails.isInAustralia === 'yes' ||
      (values.foreignPassportDetails.isInAustralia === 'no' &&
        values.foreignPassportDetails.isAuPermanentResident === 'yes')) {
      setShowDetails(true);
    }
  }, []);

  //TODO: filtering will not be required once this is implemented in new API endpoint
  const countryList = countryListResponse?.value && countryListResponse?.value['COUNTRY']
    .map(country => {
      return {
        name: country.casa_name, code: country.casa_code
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((country, index, self) =>
      index === self.findIndex(value => value.code === country.code)
    );

  const fileUploadAlert: IFormAlertType = {
    as: 'info',
    title: 'Unfortunately we can only verify foreign passports for applicants currently in Australia',
    message: 'If you are travelling to Australia soon, you may want to wait and resubmit your application then. This will enable you to complete your application within minutes, otherwise you will have to wait up to five business days for your aviation reference number to be issued.'
  };

  if (!countryList || countryList.length <= 0) {
    return <Loader message='Working. Please wait.....' />;
  }

  const redirectToManualPOI = () => {
    setPreviousStep('IdentificationDetails');
    openStepById('ManualPOIInformation');
  };
  const handleOnClickNext = async () => {
    const validationErrors = await validateForm();

    // if there is validation error for isInAustralia, or isAuPermanentResident field don't show the details page
    // enable formik on change validation
    if (validationErrors && (validationErrors?.foreignPassportDetails?.isInAustralia
      || validationErrors?.foreignPassportDetails?.isAuPermanentResident)) {
      setShowDetails(false);
      setOnChangeValidation(true);
      return;
    }

    setErrors({
      foreignPassportDetails: {}
    });
    setOnChangeValidation(false);
    const showUpload = values.foreignPassportDetails.isInAustralia === 'no' &&
      values.foreignPassportDetails.isAuPermanentResident === 'no';
    // enable file upload screen for manual dvs check
    if (showUpload) {
      setFormAlert(fileUploadAlert);
      redirectToManualPOI();
      return;
    }
    setShowDetails(true);
    setFormAlert(null);
  };

  const handleBackAction = () => {
    setShowDetails(false);
    setFormAlert(null);
  };

  const handleBackToIdentitySelection = () => {
    setFormAlert(null);
    openStepById('IdentityVerification');
  };

  const isInAustralia = values.foreignPassportDetails.isInAustralia;
  const dvsValidationIndicator = getFieldMeta('foreignPassportDetails.dvsValidationIndicator').value;
  const isLocked = dvsValidationIndicator === 'Passed';

  return (
    <>
      {
        !showDetail &&
        <>
          <ApplicationName name='Foreign passport details' />
          <AuDirectionLink
            onClick={handleBackToIdentitySelection}
            text="Back"
            direction="left" />
          <ControlInputGroup
            name='foreignPassportDetails.isInAustralia'
            label='Are you currently in Australia:'
            type='radio'
            block
            disabled={isLocked}
            options={[
              {
                label: 'Yes',
                value: 'yes',
                id: 'radio-foreignPassportDetail-isInAustralia-yes',
              },
              {
                label: 'No',
                value: 'no',
                id: 'radio-foreignPassportDetail-isInAustralia-no',
              }
            ]}
          />
          {isInAustralia && isInAustralia === 'no' &&
            <ControlInputGroup
              name='foreignPassportDetails.isAuPermanentResident'
              label='Are you an Australian permanent resident?:'
              type='radio'
              block
              disabled={isLocked}
              options={[
                {
                  label: 'Yes',
                  id: 'radio-foreignPassportDetail-isAuPermanentResident-yes',
                  value: 'yes'
                },
                {
                  label: 'No',
                  id: 'radio-foreignPassportDetail-isAuPermanentResident-no',
                  value: 'no'
                }
              ]}
            />}
          <Button as='primary' onClick={handleOnClickNext}>Next</Button>
        </>
      }
      {
        showDetail &&
        <div>
          <ApplicationName name={'Foreign passport details'} />
          <AuDirectionLink onClick={handleBackAction} text="Back" direction="left" />
          <AuTextInput
            name='foreignPassportDetails.familyName'
            label='Family name'
            hintText='Please enter your family name (surname) as written on your passport'
            as='input'
            block
            disabled={isLocked} />
          <AuTextInput
            name='foreignPassportDetails.givenNames'
            label='Given names'
            hintText='Please enter all given names as written on your passport, including any middle names'
            as='input'
            block
            disabled={isLocked} />
          <AuDateInput
            name='foreignPassportDetails.dateOfBirth'
            label='Date of birth'
            hintText='For example, 31 01 1990'
            disabled={isLocked} />
          <AuTextInput
            name='foreignPassportDetails.passportNumber'
            label='Passport number'
            hintText='Please enter your passport number exactly as it appears on your passport'
            as='input'
            block
            disabled={isLocked} />
          <AuSelect
            name={'foreignPassportDetails.nationalityOnPassport'}
            label='Nationality on passport'
            hintText='Please enter the nationality as it appears on your passport'
            block
            disabled={isLocked}
            options={[
              {
                text: 'Please select a country',
                value: ''
              },
              ...countryList.map((country: { name: string; code: string; }) => {
                return {
                  text: country.name,
                  value: country.name
                };
              })
            ]} />

          <AuFormItemWrapper>
            <AuCallout title='' style={{ paddingTop: '0' }}>
              <AuControlInput
                name='foreignPassportDetails.declaration'
                label=''
                id='checkbox-foreignPassportDetails-declaration'
                type='checkbox'
                disabled={isLocked}
                hintText='I confirm that I am authorised to provide the personal details presented and I consent to my information being checked with the document issuer or official record holder via third party systems for the purpose of confirming your identity'
              />
              {declarationFieldError && <AuErrorText text={declarationFieldError} />}
            </AuCallout>
          </AuFormItemWrapper>
          <FormNavigation nextButtonText='Next' hidePrevious onNextAction={handleNextAction} />
        </div>
      }
    </>
  );
};

export default ForeignPassportDetails;